<!-- HISTORY:
- V260511.1: Added idResolutionsData prop to display names of specified ID fields.
- V260320.1: Removed chartsSettings prop and added labels instead.
- V260312.1: Added background styling to the dimension headers (modelDims).
- V260226.1: Fixed a bug in init() that caused a console error in some cases.
- V260224.1: Supported loading by extending the overridden tbody and raising a ready state.
- V260223.1: Implemented new business logic to handle cell clicks (instead of row click) by sending extra data to BtChartForLS.
- V251205.1: [not sent to RF] Fixed a bug that didn't add calculated columns at the beginning + Bypassed init() in edit mode.
- V251204.1: 1st Release (was copied from BtCalculatedTableForCs to make it suitable for BtChartForLS use).
-->
<!--TODO:
   - Implement commented fields (columns sequence, sort column, etc).
-->
<!--
   font-weight-black, font-weight-bold, font-italic
   text-left, text-center, text-right
   text-decoration-none, text-decoration-underline
   text--primary, text--secondary, text--disabled
-->
<template>
   <!--V260224: :loading="loading || preparing"
   loading-text="Loading data. Please wait..."
   no-data-text="No data are available."
   no-results-text="No matching data were found." -->
   <v-data-table dense
      class="elevation-1 mb-2"
      :footer-props="footerProps"
      :headers="tableHeaders"
      :hide-default-footer="tableData.length <= footerProps.itemsPerPageOptions[0]"
      :hide-default-header="false"
      :items="tableData"
      item-key="__seq"
      :items-per-page="itemsPerPage"
      :key="tableRenderKey"
   >
      <template v-slot:body="{ items }">
         <!--V260224: Loading state -->
         <tbody v-if="loading">
            <tr>
               <td :colspan="tableHeaders.length" class="text-center">
                     Loading data. Please wait...
               </td>
            </tr>
         </tbody>
         <!--V260224: No data -->
         <tbody v-else-if="!items.length">
            <tr>
               <td :colspan="tableHeaders.length" class="text-center">
                  No data are available.
               </td>
            </tr>
         </tbody>
         <tbody v-else>
            <!-- @click="rowClicked(item, i)" -->
            <!-- :class="{ 'clicked-row': clickedRowIndex === i }" -->
            <tr v-for="(item, i) in items" :key="item.__seq">
               <!-- Moved :class here from <div> -->
               <td v-for="(header, j) in tableHeaders"
                  :key="header.value"
                  class="px-0"
                  :class="modelDims.includes(header.value) ? 'blue-grey lighten-5' : ''"
                  style="vertical-align: middle;"
               >
                  <div
                     :style="`height: 97%; display: flex; align-items: center; justify-content: space-between; padding-left: 0 !important; padding-right: 0 !important; ${getCellStyle(header.value, i)}`"
                  >
                     <div v-if="modelDims.includes(header.value)"
                        class="pl-4 pr-0 d-flex black--text"
                        style="text-align: left; flex-grow: 1;"
                     >{{ item[header.value] }}</div>
                     <div v-else-if="!item[header.value] || isNaN(item[header.value]) || !Number.isFinite(item[header.value])"></div>
                     <div v-else-if="calculatedColumns?.find(cc => cc.name === header.value)"
                        class="pr-4 d-flex justify-end black--text"
                        style="text-align: right; flex-grow: 1;"
                     >{{ formatValue(item[header.value], header.value) }}</div>
                     <!-- V260223 - start -->
                     <!-- class="pr-4 d-flex justify-end black--text" -->
                     <!-- @click="cellClicked(item[tableHeaders[0].value], header.value, i, j, header.isBlended)" -->
                     <!-- <div v-else-if="isEditing"
                        style="text-decoration: none; text-align: right; flex-grow: 1;"
                        class="pr-4 d-flex justify-end black--text"
                     >{{ formatValue(item[header.value], header.value) }}</div> -->
                     <a v-else
                        style="text-decoration: none; text-align: right; flex-grow: 1;"
                        :class="`pr-4 d-flex justify-end ${i != lastClickedRowInd || j != lastClickedColInd ? 'black--text' : 'blue--text font-italic font-weight-bold'}`"
                        href="#"
                        @click="cellClicked(item, header.value, i, j)"
                     >{{ formatValue(item[header.value], header.value) }}</a>
                     <!-- V260223 - end -->
                  </div>
               </td>
            </tr>
            <tr class="blue-grey lighten-4">
               <td class="pl-4 font-weight-bold">Total:</td>
               <td v-for="(header, i) in tableHeaders.slice(1)" :key="i" class="px-0 py-3">
                  <div v-if="hasOwn(header, 'TotalCol_' + header.value)">
                     <span v-if="Number(header['TotalCol_' + header.value])"
                        class="font-weight-bold pr-4 d-flex justify-end"
                     >{{formatValue(header['TotalCol_' + header.value], header.value)}}</span>
                  </div>
               </td>
            </tr>
         </tbody>
      </template>
   </v-data-table>
</template>

<script>
import { hasOwn } from '../mixins/bt-mixin.js';

const NAME = "BtCalculatedTableForLS";
const MSG = `-----${NAME} V260312.1 says => `;

function _findVariables(expression) {
   const startMark = "{{";
   const endMark = "}}";
   const regEx = /{{[^{]+}}/g;
   const matches = expression.match(regEx) || [];
   const vars = [];

   matches.forEach(match => {
      const key = match.replace(startMark, '').replace(endMark, '');
      if (key) vars.push(key);
   });

   return vars;
}

export default {
   name: NAME,

   props: {
      calculatedColumns: {
         //[{name:'',expression:'1 or more {{}}',fix:'none::|pre::text|post::text',position:'s::|e::|b::name|a::name'}
         type: Array,
         required: true,
         default: () => []
      },
      clickedRowIndex: {
         type: Number,
         required: true
      },
      columnsSequence: {
         type: Array,
         default: () => []
      },
      originalData: {
         type: Array,
         required: true,
         default: () => []
      },
      title: {
         type: String
      },
      /* V260320 - start */
      // chartsSettings: {
      //    type: Object
      // },
      labels: {
         type: Array
      },
      /* V260320 - end */
      debug: {
         type: Boolean,
         default: false
      },
      exportData: {
         type: Boolean,
         default: false
      },
      isEditing: {
         type: Boolean,
         required: true
      },
      modelDims: {
         type: Array,
         required: true
      },
      modelLabels: {
         type: Array,
         required: true
      },
      footerProps: {
         type: Object,
         default: () => { 
            return {
               itemsPerPageOptions: [5, 10, 20],
               showFirstLastPage: true
            }
         }
      },
      itemsPerPage: {
         type: Number,
         default: 5
      },
      loading: {
         type: Boolean,
         default: false
      },
      sortColumn: {
         type: String
      },
      sortOrder: {
         type: String
      },
      // V260511
      idResolutionsData: {
         type: Object
      }
   },

   data() {
      return {
         tableData: [],
         tableHeaders: [],
         tableRenderKey: 0,
         lastClickedRowInd: -1,
         lastClickedColInd: -1
      }
   },

   watch: {
      originalData: {
         immediate: true,
         deep: true,
         handler() {
            // console.log(`in ${NAME}/originalData watch: val=${JSON.stringify(val || [])}`);
            this.init();
         }
      },

      calculatedColumns: {
         immediate: true,
         deep: true,
         handler() {
            // console.warn(`in ${NAME}/calculatedColumns watch: val=${JSON.stringify(val || [])}`);
            this.init();
         }
      },

      exportData: {
         immediate: false,
         deep: false,
         handler(val) {
            if (val) this.exportDataToCSV();
         }
      }
   },

   methods: {
      hasOwn,

      // // _alert(msg) {
      // //    if (this.debug) {
      // //       alert(msg);
      // //    }
      // // },

      log(msg, isError) {
         if (isError)
            console.error(`${MSG}${msg}`);
         else if (this.debug) {
            console.log(`${MSG}${msg}`);
            // // alert(`${MSG}${msg}`);
         }
      },

      getCellStyle(header, ind) {
         if (this.calculatedColumns?.find(cc => cc.name === header)) {
            if (this.clickedRowIndex === ind)
               return 'background-color: #e3f2fd !important; transition: background-color 0.2s';
            else return 'background-color: #eeeeee !important;';
         } else return '';
      },

      formatValue(val, header ='') {
         let result = new Intl.NumberFormat().format(val);
         const calCol = this.calculatedColumns?.find(cc => cc.name === header);
         if (calCol) {
            const fixParts = calCol.fix.split('::');
            if (fixParts[1] === '%') {
               result = new Intl.NumberFormat('en-US',
                  {
                     minimumFractionDigits: 2,
                     maximumFractionDigits: 2
                  }
               ).format(val);
            }
            
            if (fixParts[0] === 'pre')
               result = fixParts[1] + result;
            else if (fixParts[0] === 'post')
               result += fixParts[1];
         }
         // this.log('in formatValue: result=' + result);
         return result;
      },

      init() {
         // this.log('in init()');
         this.tableHeaders = [];
         this.tableData = [];
         if (
               this.isEditing || //V251205
               !this.originalData?.length ||
               !this.modelDims?.length ||
               !this.modelLabels?.length ||
               !this.calculatedColumns?.length
            )
            return;

         const headers = [];
         this.modelDims.forEach(dim => {
            headers.push(this.getHeader(dim, dim, 'start', 'blue-grey lighten-5'));
         });

         this.modelLabels.forEach(lbl => {
            headers.push(this.getHeader(lbl, lbl, 'end'));
         });

         const csLen = this.columnsSequence?.length || 0;
         if (csLen) {
            for (let index = 1; index < headers.length; index++) {
               const element = headers[index];
               // using '==' instead of '===' because value types are different.
               const seq = this.columnsSequence.findIndex(cs => cs == element.value);
               element.seq = seq > -1 ? seq + 1 : index + csLen;
            }
            headers.sort((a, b) => a.seq - b.seq);
         }

         //V260511: const data = [...this.originalData];
         const data = this.originalData.map(row => {
            const newRow = { ...row };
            for (const [dim, resolutions] of Object.entries(this.idResolutionsData)) {
               if (newRow[dim] !== undefined) {
                  const match = resolutions.find(r => String(r.v) === String(newRow[dim]));
                  if (match)
                     newRow[dim] = match.f;
               }
            }
            return newRow;
         });

         // alert('calculated Data=' + JSON.stringify(data))

         if (this.sortColumn && Object.keys(data[0]).includes(this.sortColumn)) {
            if (this.sortOrder === 'asc')
               data.sort((a, b) => ((a[this.sortColumn] || 0) > (b[this.sortColumn] || 0)) ? 1 : -1);
            else
               data.sort((a, b) => ((a[this.sortColumn] || 0) < (b[this.sortColumn] || 0)) ? 1 : -1);
         }

         // data.forEach((row, i) => {
         //    Object.keys(row).forEach(key => {
         //       const totalKey = 'TotalCol_' + key;
         //       const val = row[key];
         //       if (!isNaN(val) && typeof val === 'number') {
         //          const keyHeader = headers.find(h => h.value === key);
         //          if (keyHeader) {
         //             if (hasOwn(keyHeader, totalKey))
         //                keyHeader[totalKey] += val;
         //             else
         //                keyHeader[totalKey] = val;
         //          }
         //       }
         //    });
         //    row.__seq = i;
         // });

         data.forEach((row, i) => {
            Object.keys(row).filter(key => ![...this.modelDims, '__seq'].includes(key)).forEach(key => {
               const totalKey = 'TotalCol_' + key;
               const val = row[key];
               if (!isNaN(val) && typeof val === 'number') {
                  const keyHeader = headers.find(h => h.value === key) || {}; //V260226: set empty obj
                  if (hasOwn(keyHeader, totalKey))
                     keyHeader[totalKey] += val;
                  else
                     keyHeader[totalKey] = val;
               }
            });
            row.__seq = i;
         });

         let sSeq = 0;
         this.calculatedColumns?.forEach(calCol => {
            const header = this.getHeader(calCol.name, calCol.name, 'end', 'grey lighten-3');   //true
            const positionParts = calCol.position.split('::');
            if (positionParts[0] === 's') {
               //V251205: headers.splice(1 + sSeq++, 0, header);
               headers.splice(this.modelDims.length + sSeq++, 0, header);
            } else {
               const ind = headers.findIndex(h => h.value.toString().toLowerCase() === positionParts[1].toString().toLowerCase());
               if (ind === -1)
                  headers.push(header);
               else if (ind === 0)
                  headers.splice(1, 0, header);
               else {
                  if (positionParts[0] === 'b')
                     headers.splice(ind, 0, header);
                  else
                     headers.splice(ind + 1, 0, header);
               }
            }

            const calColHeaders = _findVariables(calCol.expression);
            data.forEach(d => {
               let resolvedExpr = calCol.expression;
               calColHeaders.forEach(header => {
                  if (hasOwn(d, header))
                     resolvedExpr = resolvedExpr.replace('{{' + header + '}}', d[header]);
                  else {
                     const totalKey = header.replace('$$', 'TotalCol_');
                     const totalHeader = headers.find(h => hasOwn(h, totalKey));
                     if (totalHeader)
                        resolvedExpr = resolvedExpr.replace('{{' + header + '}}', totalHeader[totalKey]);
                     else {
                        this.log(`'${header}' not found!`);
                        resolvedExpr = resolvedExpr.replace('{{' + header + '}}', 0);
                     }
                  }
               });

               d[calCol.name] = eval(resolvedExpr);
            });
         });

         //for resolving total row
         this.calculatedColumns?.forEach(calCol => {
            const calColHeaders = _findVariables(calCol.expression);
            let resolvedExpr = calCol.expression;
            let error = '';
            calColHeaders.forEach(header => {
               let totalKey = 'TotalCol_' + header;
               let totalHeader = headers.find(h => hasOwn(h, totalKey));
               if (totalHeader)
                  resolvedExpr = resolvedExpr.replace('{{' + header + '}}', totalHeader[totalKey]);
               else {
                  totalKey = header.replace('$$', 'TotalCol_');
                  totalHeader = headers.find(h => hasOwn(h, totalKey));
                  if (totalHeader)
                     resolvedExpr = resolvedExpr.replace('{{' + header + '}}', totalHeader[totalKey]);
                  else {
                     error = `'${header}' not found!`;
                     return;
                  }
               }
            });

            const th = headers.find(h => h.text === calCol.name);
            th['TotalCol_' + calCol.name] = error ? error : eval(resolvedExpr);
         });

         this.tableHeaders = [...headers];
         this.tableData = [...data];
         this.tableRenderKey++;
         this.$emit('ready', true); // V260224
      },

      getHeader(label, id, align, headerClass) {   //addClass
         //V260320: const lbl = this.chartsSettings?.labels?.find(l => l.id == label);
         const lbl = this.labels?.find(l => l.id == label);
         const header = { 
            text: lbl?.label || label,
            value: id,
            align: align,
            sortable: true
         };

         // if (addClass)
         //    header.class = 'grey lighten-3';
         if (headerClass)
            header.class = headerClass;

         return header;
      },

      // V260223
      // rowClicked(row, ind) {
      //    // console.warn(`in rowClick(): row=${row}, ind=${ind}`);
      //    this.$emit('click', row, ind, this.modelDims);
      // },

      // V260223
      cellClicked(row, colName, rowInd, colInd) {
         // alert(`in cellClicked(): row=${JSON.stringify(row)}, colName=${colName}, rowInd=${rowInd}, colInd=${colInd}`);
         if (this.isEditing) return;

         if (rowInd === this.lastClickedRowInd && colInd === this.lastClickedColInd) {
            this.lastClickedRowInd = -1;
            this.lastClickedColInd = -1;
            this.$emit('reclick');
         } else {
            this.lastClickedRowInd = rowInd;
            this.lastClickedColInd = colInd;
            /* V260511 - start: retrieving the original row with id instead of name */
            // this.$emit('click', row, rowInd, this.modelDims, colName);
            const originalRow = { ...row };
            for (const [dim, resolutions] of Object.entries(this.idResolutionsData)) {
               if (originalRow[dim] !== undefined) {
                  const match = resolutions.find(r => r.f === originalRow[dim]);
                  if (match)
                     originalRow[dim] = match.v;
               }
            }

            this.$emit('click', originalRow, rowInd, this.modelDims, colName);
            /* V260511 - end */
         }
      },

      exportDataToCSV() {
         const headers = this.tableHeaders.map(h => h.text);            
         const rows = this.tableData.map(row =>
            headers.map(header => {
               const matching = this.tableHeaders.find(h => h.text === header);
               const val = row[matching.value];
               const finalVal = val == undefined || val == null || Number.isNaN(val) || val === 0 || val === Infinity || val === -Infinity ? "" : val;
               return JSON.stringify(finalVal);
            }).join(",")
         );

         const csvContent = [headers.join(","), ...rows].join("\n");
         const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
         const link = document.createElement("a");
         link.href = URL.createObjectURL(blob);
         link.setAttribute("download", `Exported Data for ${this.title}.csv`);
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         this.$emit('export');
      }
   },

   created() {
      // alert(`in created(): calculatedColumns=${JSON.stringify(this.calculatedColumns)}`);
      // this.log(`in created(): originalData=${JSON.stringify(this.originalData)}`);
   }
}
</script>

<style scoped>
.clicked-row {
   background-color: #e3f2fd !important;  /* rgba(33, 150, 243, 0.15); light blue */
   transition: background-color 0.2s;
}
</style>
